import { useLayoutEffect, useRef, useState } from 'react';
import { Textarea } from '@/components/Textarea';
import useUpdateJobSource from '../../api/useUpdateJobSource';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import LinkedAccountEditFieldModal from './LinkedAccountEditFieldModal';

const MAX_HEIGHT = 180;

type Props = {
  isOpen: boolean;
  jobSourceId: number;
  currentAccessUrl: string | null;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
};

export default function LinkedAccountEditAccessUrlModal({
  isOpen,
  jobSourceId,
  currentAccessUrl,
  onClose,
  onSaved,
}: Props) {
  const [accessUrl, setAccessUrl] = useState(currentAccessUrl ?? '');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const updateJobSource = useUpdateJobSource();

  const resize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  };

  const setTextareaRef = (el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    resize(el);
  };

  // Dialog のマウント直後は scrollHeight が確定しないため次フレームで測り直す
  useLayoutEffect(() => {
    resize(textareaRef.current);
    const id = requestAnimationFrame(() => resize(textareaRef.current));
    return () => cancelAnimationFrame(id);
  }, [accessUrl, isOpen]);

  const handleSubmit = async () => {
    try {
      await updateJobSource.mutateAsync({ id: jobSourceId, access_url: accessUrl });
      await onSaved();
      showSuccessToast('アカウント情報を更新しました');
      onClose();
    } catch {
      showErrorToast('アカウント情報の更新に失敗しました');
    }
  };

  return (
    <LinkedAccountEditFieldModal
      isOpen={isOpen}
      title="アカウント情報を編集"
      isPending={updateJobSource.isPending}
      canSubmit={accessUrl.trim() !== ''}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-xs">
        <label className="text-heading-xxs">アクセスURL</label>
        <Textarea
          ref={setTextareaRef}
          value={accessUrl}
          onChange={(e) => setAccessUrl(e.target.value)}
          className="max-h-[180px] min-h-[42px] w-full resize-none overflow-y-auto text-body-sm break-all"
          disabled={updateJobSource.isPending}
        />
      </div>
    </LinkedAccountEditFieldModal>
  );
}
